import { Check, X } from 'lucide-react';

import { usePricing } from '@/contexts/PricingContext';

const RYDER_PRODUCT_IMAGE = 'https://ryder.id/cdn/shop/files/product-gallery-image-Feb19.jpg?v=1771434727&width=400';

const BtcComparisonTable = () => {
  const pricing = usePricing();
  const accentColor = '#6a63f5'; // Ryder brand purple
  const backgroundColor = '#0a0a0f';
  
  const features = [
    { name: 'Setup Time', ryder: '60 seconds', ledger: '15-20 minutes', tangem: '2-3 minutes' },
    { name: 'Display', ryder: '1.6" AMOLED', ledger: '2.8" E-ink (Flex)', tangem: 'None (phone only)' },
    { name: 'Seed Phrase', ryder: 'Optional', ledger: 'Required', tangem: 'Optional' },
    { name: 'Recovery Method', ryder: 'NFC Recovery Tag', ledger: '24-word phrase', tangem: 'Backup cards' },
    { name: 'Phone Recovery Backup', ryder: true, ledger: false, tangem: false },
    { name: 'Social Recovery', ryder: true, ledger: false, tangem: false },
    { name: 'On-Device Verification', ryder: true, ledger: true, tangem: false },
    { name: 'Multi-Chain Support', ryder: 'BTC, ETH, SOL, STX + tokens', ledger: '5,500+ coins', tangem: '16,000+ tokens' },
    { name: 'Connectivity', ryder: 'NFC', ledger: 'USB-C + Bluetooth', tangem: 'NFC' },
    { name: 'Charging', ryder: 'Qi Wireless', ledger: 'USB-C', tangem: 'Battery-free' },
    { name: 'Water Resistant', ryder: true, ledger: false, tangem: true },
    { name: 'Security Chip', ryder: 'Infineon EAL6+', ledger: 'ST EAL6+', tangem: 'Samsung EAL6+' },
    { name: 'Cashback Program', ryder: '$200 BTC cashback', ledger: 'None', tangem: 'None' },
    { name: 'Price', ryder: `$${pricing.salePrice}`, ledger: '$79-$249', tangem: '$55-$100' },
    { name: 'Effective Cost', ryder: 'Effectively $0', ledger: '$79-$249', tangem: '$55-$100' },
  ];

  const renderCell = (value: string | boolean, isRyder: boolean = false) => {
    if (typeof value === 'boolean') {
      return value ? (
        <Check className={`w-5 h-5 ${isRyder ? 'text-green-400' : 'text-white/60'}`} />
      ) : (
        <X className="w-5 h-5 text-red-400/60" />
      );
    }
    if (value === '$200 BTC cashback' || value === 'Effectively $0') {
      return <span className="font-bold text-[#6a63f5] text-xs sm:text-sm">{value}</span>;
    }
    if (value === 'None') {
      return <span className="text-white/40 italic text-xs sm:text-sm">{value}</span>;
    }
    return <span className={`text-xs sm:text-sm ${isRyder ? 'font-medium' : 'text-white/70'}`}>{value}</span>;
  };

  return (
    <section className="py-16 text-white" style={{ backgroundColor }}>
      <div className="container px-4">
        <div className="text-center mb-12">
          <div className="text-sm font-medium mb-2" style={{ color: accentColor }}>
            How We Compare
          </div>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight leading-tight mb-4">
            Ryder One vs. The Competition
          </h2>
          <p className="text-white/70 max-w-2xl mx-auto">
            The best security, the simplest experience, and a <span className="text-[#6a63f5] font-semibold">$200 Bitcoin cashback</span> program.
          </p>
        </div>

        {/* Unified Table - scrollable on mobile */}
        <div className="relative">
          {/* Scroll hint gradient on mobile */}
          <div className="md:hidden absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-[#0a0a0f] to-transparent z-10 pointer-events-none" />
          
          <div className="overflow-x-auto scrollbar-hide -mx-4 px-4">
            <table className="w-full max-w-5xl mx-auto border-collapse" style={{ minWidth: '580px' }}>
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-3 md:py-4 px-2 md:px-4 text-white/60 font-medium text-xs md:text-sm sticky left-0 z-20" style={{ backgroundColor, minWidth: '100px' }}>Feature</th>
                  <th 
                    className="py-3 md:py-4 px-3 md:px-6 text-center relative"
                    style={{ 
                      backgroundColor: `${accentColor}15`,
                      borderTop: `2px solid ${accentColor}`,
                      borderLeft: `1px solid ${accentColor}30`,
                      borderRight: `1px solid ${accentColor}30`,
                      minWidth: '120px',
                    }}
                  >
                    <div className="flex flex-col items-center gap-1 md:gap-2">
                      <div className="w-10 h-10 md:w-16 md:h-16 rounded-lg md:rounded-xl overflow-hidden bg-white/10 shadow-lg" style={{ boxShadow: `0 4px 20px ${accentColor}30` }}>
                        <img 
                          src={RYDER_PRODUCT_IMAGE} 
                          alt="Ryder One" 
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      </div>
                      <div className="font-bold text-sm md:text-lg" style={{ color: accentColor }}>Ryder One</div>
                      <span className="inline-flex items-center gap-1 px-1.5 md:px-2 py-0.5 rounded-full bg-[#6a63f5]/20 text-[#6a63f5] text-[9px] md:text-[10px] font-medium">
                        + $200 BTC cashback
                      </span>
                    </div>
                  </th>
                  <th className="py-3 md:py-4 px-2 md:px-4 text-center text-white/80 font-medium text-xs md:text-base" style={{ minWidth: '100px' }}>Ledger</th>
                  <th className="py-3 md:py-4 px-2 md:px-4 text-center text-white/80 font-medium text-xs md:text-base" style={{ minWidth: '100px' }}>Tangem</th>
                </tr>
              </thead>
              <tbody>
                {features.map((feature, index) => {
                  const isHighlightRow = feature.name === 'Cashback Program' || feature.name === 'Effective Cost';
                  return (
                    <tr 
                      key={index} 
                      className={`border-b border-white/5 transition-colors ${isHighlightRow ? 'bg-[#6a63f5]/5' : 'hover:bg-white/5'}`}
                    >
                      <td className={`py-3 md:py-4 px-2 md:px-4 font-medium text-xs md:text-sm sticky left-0 z-20 ${isHighlightRow ? 'text-[#6a63f5]' : 'text-white/80'}`} style={{ backgroundColor: isHighlightRow ? '#0d0d1a' : backgroundColor }}>
                        {feature.name}
                      </td>
                      <td 
                        className="py-3 md:py-4 px-3 md:px-6 text-center"
                        style={{ 
                          backgroundColor: isHighlightRow ? `${accentColor}20` : `${accentColor}10`,
                          borderLeft: `1px solid ${accentColor}30`,
                          borderRight: `1px solid ${accentColor}30`,
                          ...(index === features.length - 1 ? { borderBottom: `2px solid ${accentColor}` } : {}),
                        }}
                      >
                        <div className="flex items-center justify-center">
                          {renderCell(feature.ryder, true)}
                        </div>
                      </td>
                      <td className="py-3 md:py-4 px-2 md:px-4 text-center">
                        <div className="flex items-center justify-center">
                          {renderCell(feature.ledger)}
                        </div>
                      </td>
                      <td className="py-3 md:py-4 px-2 md:px-4 text-center">
                        <div className="flex items-center justify-center">
                          {renderCell(feature.tangem)}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-12">
          <p className="text-white/60 text-sm">
            Same premium security. Only Ryder One includes a <span className="text-[#6a63f5] font-semibold">$200 Bitcoin cashback</span>.
          </p>
        </div>
      </div>
    </section>
  );
};

export default BtcComparisonTable;
